import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  MAX_BOAT_NAME_LENGTH,
  UNTITLED_BOAT_NAME,
  boatDisplayName,
  labelFromBoatFilename,
} from '../../sim/boat/index.ts';
import { Tooltip } from '../tooltip.tsx';

export function BoatNameField({
  name,
  fileName,
  onRename,
  noun = 'boat',
}: {
  name: string;
  fileName: string;
  onRename: (raw: string) => void;
  /** What is being named, for the labels. The bot workshop shares this field. */
  noun?: 'boat' | 'bot';
}): JSX.Element {
  const label = noun === 'bot' ? 'Bot name' : 'Boat name';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const canceling = useRef(false);
  const committed = useRef(false);
  const display = boatDisplayName(name, labelFromBoatFilename(fileName) || UNTITLED_BOAT_NAME);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const begin = (): void => {
    committed.current = false;
    canceling.current = false;
    setDraft(name.trim() ? name : display === UNTITLED_BOAT_NAME ? '' : display);
    setEditing(true);
  };

  const cancel = (): void => {
    canceling.current = true;
    setEditing(false);
  };

  const commit = (): void => {
    if (committed.current) {
      return;
    }

    committed.current = true;
    const next = draft.trim();

    if (next === name.trim() || (name.trim() === '' && next === display)) {
      cancel();
      return;
    }

    onRename(draft);
    setEditing(false);
  };

  return (
    <div class="editor-map-name" data-file-name={fileName} data-boat-name={display}>
      {editing ? (
        <input
          ref={inputRef}
          class="editor-map-name-input"
          type="text"
          aria-label={label}
          value={draft}
          maxlength={MAX_BOAT_NAME_LENGTH}
          spellcheck={false}
          onInput={(event) => setDraft((event.target as HTMLInputElement).value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.stopPropagation();
              commit();
            } else if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              cancel();
            }
          }}
          onBlur={() => {
            if (canceling.current) {
              canceling.current = false;
              return;
            }

            commit();
          }}
        />
      ) : (
        <Tooltip label={`Rename ${noun}`} side="bottom">
          <button
            type="button"
            class="editor-map-name-button"
            aria-label={label}
            onClick={begin}
          >
            {display}
          </button>
        </Tooltip>
      )}
    </div>
  );
}
